//M10-12866
import { Transition } from "@headlessui/react";
import { FilterIcon } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Input, Spinner } from "src/components";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select.component";
import { RootState } from "src/context/store";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import { usePagination } from "src/hooks/usePagination";
import { getAllCompanyDetails } from "src/services/company-api";
import { fetchOrderPoDetails, fetchPoStatusDetails, fetchStockistFilterData, getCounterPoDetails } from "src/services/order-api";
import { getCounterList } from "src/services/po-api"; //M10-15868
import { ordPoActions } from "src/context/order-po-slice";
import OrdersPOFilter from "../../container/OrderPoFilter";
//M10-13811
import OrderSelectDialog from "./OrderSelectDialog";
import { useToast } from "src/hooks/useToast";
//M10-13811
//PO-LIST-CARD-REDESIGN
import PoCard from "./PoCard";
import downArrow from "src/assets/down-arrow.svg";
//PO-LIST-CARD-REDESIGN


//M10-15818
const poSearchOptions = [
    { label: "PO ID Number", value: "poidNumber" },
    { label: "Stockist Name", value: "stockistName" },
    { label: "Counter Code", value: "counterCode" },
    { label: "Counter Name", value: "counterName" },
    { label: "FUID No", value: "fuidNo" },
    { label: "Order Code", value: "orderCode" },
    { label: "PO Ref No", value: "poidRefNumber" },
];
//M10-15818

//PO-LIST-CARD-REDESIGN: mirrors Pagination.tsx's generatePageNumbers, but driven directly off
// pageIndex/pageCount (1-based) instead of a tanstack table instance, since the card list no
// longer goes through the generic Table component.
const generatePageNumbers = (pageIndex: number, pageCount: number): (number | string)[] => {
    const maxPagesToShow = 3;
    let pages: (number | string)[] = [];

    if (pageCount <= maxPagesToShow) {
        pages = Array.from({ length: pageCount }, (_, i) => i + 1);
    } else {
        const startPage = Math.max(1, pageIndex - 1);
        const endPage = Math.min(pageCount, pageIndex + 2);

        if (startPage > 1) {
            pages.push(1);
            if (startPage > 2) pages.push("...");
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        if (endPage < pageCount) {
            if (endPage < pageCount - 1) pages.push("...");
            pages.push(pageCount);
        }
    }

    return pages;
};
//PO-LIST-CARD-REDESIGN

const PoOrderScreen = () => {
    const [totalRowCount, setTotalRowCount] = useState(0);
    const { onPaginationChange, pageSize, pageIndex, resetPagination } = usePagination();
    const navigate = useNavigate();
    const [_, setSearchParams] = useSearchParams();
    const [isFiltersShown, setIsFiltersShown] = useState<boolean>(false);
    const { OrderPoFilters, OrdersPoFiltersData, orderPoData } = useSelector((state: RootState) => state.ordPoState);
    //M10-15818
    const [searchCategory, setSearchCategory] = useState<string>(poSearchOptions[0].value);
    const [searchValue, setSearchValue] = useState("");
    //M10-15818

    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    //M10-13811
    const { toast } = useToast();
    const [orderSelectState, setOrderSelectState] = useState<{
        isOpen: boolean;
        orderCodes: string[];
        companyCode?: number | string;
        stockist_po_attachment_id?:number | string;
        po_file_path?: string;
        autoOpenPoView?: boolean;
    }>({
        isOpen: false,
        orderCodes: [],
    });
    //M10-13811


    const ref = useOutsideClick(() => {
        setIsFiltersShown(false);
    });

    const fetchPendingOrder = async () => {
        try {
            setLoading(true);
            const res = await fetchOrderPoDetails({
                companyCode: OrderPoFilters?.company?.[0]?.code,
                poidStatusId: OrderPoFilters?.status?.[0]?.poid_status_id,  //M10-13811
                stockist_company_lnk_id: OrderPoFilters?.stockist?.[0]?.stockist_company_lnk?.[0]?.id ?? "",
                counterCode: OrderPoFilters?.counter?.[0]?.counter_code, //M10-15868
                page_number: pageIndex,
                page_size: pageSize,
                //M10-15818
                ...(searchValue && searchCategory ? { [searchCategory]: searchValue } : {}),
                //M10-15818
            });
            setTotalRowCount(res?.data?.total);
            setSearchParams({
                pageSize: pageSize.toString(),
                pageIndex: pageIndex.toString(),
            });
            console.log(res?.data?.data, "res?.data?.data")
            dispatch(ordPoActions.setOrderPoData(res?.data?.data))
            setLoading(false);
        } catch (error) {
            console.error("Failed to fetch pending orders:", error);
        } finally {
            setLoading(false);
        }
    };

    let pageCount = Math.ceil(totalRowCount / (pageSize ?? 1));
    if (totalRowCount % 10 === 0) {
        pageCount--;
    }
    //M10-13811
    // Parse a comma-separated "order_codes" string into a clean list.
    // Handles null/undefined/empty/whitespace-only values safely.
    const parseOrderCodes = (raw: string | null | undefined): string[] => {
        if (!raw || typeof raw !== "string") return [];
        return raw
            .split(",")
            .map((c) => c.trim())
            .filter(Boolean);
    };

    // Navigate to the Order Screen (Order Details tab) with a flag that tells
    // OrderInfoDetailsTab to auto-open the "Edit PO Details" modal on mount.
    // Persists company_code to sessionStorage so getOrderDetailsByOrderCode2
    // can send it in the request header (matches the existing fallback chain
    // in OrderInfoDetailsTab.fetchOrderId).
    const openOrderEditScreen = (orderCode: string, companyCode?: number | string,stockist_po_attachment_id?:number|string, autoOpenPoView: boolean = false, po_file_path?: string) => {
        if (companyCode != null && companyCode !== "") {
            window.sessionStorage.setItem("companyFilter", String(companyCode));
        }
        navigate(`/transactions/orders/info/${orderCode}/1`, {
            state: { autoOpenPoEdit: true, autoOpenPoView, companyCode,stockist_po_attachment_id, po_file_path },
        });
    };

    // Fetches the previously saved counter PO (Processed rows with no linked order yet) and reopens the PO
    // Products screen pre-filled, so the KAM can review/edit and resubmit via the same counter_po endpoint.
    const openProcessedPoScreen = async (original: any) => {
        const stockistPoAttachmentId = original?.stockist_po_attachment_id;
        if (!stockistPoAttachmentId) {
            toast({ variant: "destructive", description: "No orders are linked to this PO." });
            return;
        }

        try {
            setLoading(true);
            const res = await getCounterPoDetails(stockistPoAttachmentId, original?.company_code);
            const counterPoData = res?.data?.data?.data;

            if (!counterPoData) {
                toast({ variant: "destructive", description: "Unable to load PO details." });
                return;
            }

            // kam_type only comes back nested under novu_metadata — PoOrderProductScreent reads it flat
            // (OrderPoDetails?.kam_type) to decide whether to render the digitize flow at all, so surface it here.
            const mergedOrderPoDetails = {
                ...original,
                ...counterPoData,
                kam_type: counterPoData?.novu_metadata?.kam_type || original?.kam_type,
            };

            window.sessionStorage.setItem("counterCompanyLocationId", original?.counter_company_location_id);
            navigate(`/transactions/orders/po/products/${+original?.counter_company_location_id}`, {
                state: { OrderPoDetails: mergedOrderPoDetails, existingCounterPoData: counterPoData },
            });
        } catch (error) {
            console.error("Failed to fetch counter PO details:", error);
            toast({ variant: "destructive", description: "Unable to load PO details." });
        } finally {
            setLoading(false);
        }
    };

    // M10-15868: clicking the "Order Codes" value on a PO card should jump straight to that
    // order — single code navigates directly, multiple codes let the user pick one, same as the
    // (currently disabled) status 2/4 branch below.
    const handleOrderCodeClick = (original: any) => {
        const codes = parseOrderCodes(original?.order_codes);
        if (codes.length === 0) return;

        if (codes.length === 1) {
            openOrderEditScreen(codes[0], original?.company_code, original?.stockist_po_attachment_id, false, original?.po_file_path);
            return;
        }

        setOrderSelectState({
            isOpen: true,
            orderCodes: codes,
            companyCode: original?.company_code,
            stockist_po_attachment_id: original?.stockist_po_attachment_id,
            po_file_path: original?.po_file_path,
            autoOpenPoView: false,
        });
    };

    const rowClickHandler = async (row: any) => {
        const original = row?.original;
        const statusId = original?.poid_status_id;

        // Status 1 (Pending): original PO-products flow.
        if (statusId === 1) {
            window.sessionStorage.setItem("counterCompanyLocationId", original?.counter_company_location_id);
            navigate(`/transactions/orders/po/products/${+original?.counter_company_location_id}`, {
                state: { OrderPoDetails: original },
            });
            return;
        }

        // Status 2 (Approved) or 4 (Post): open linked order(s) with Edit PO modal.
        // if (statusId === 2 || statusId === 4) {
        //     const codes = parseOrderCodes(original?.order_codes);
        //     // Auto-open the Product-wise PO Quantities (View) section only for status 2.
        //     const autoOpenPoView = statusId === 2;

        //     if (codes.length === 0) {
        //         // No order linked yet (e.g. Processed) — fall back to the saved counter PO instead of erroring out.
        //         await openProcessedPoScreen(original);
        //         return;
        //     }

        //     if (codes.length === 1) {
        //         openOrderEditScreen(codes[0], original?.company_code,original?.stockist_po_attachment_id, autoOpenPoView, original?.po_file_path);
        //         return;
        //     }

        //     // Multiple orders — let the user pick one.
        //     setOrderSelectState({
        //         isOpen: true,
        //         orderCodes: codes,
        //         companyCode: original?.company_code,
        //         stockist_po_attachment_id:original?.stockist_po_attachment_id,
        //         po_file_path: original?.po_file_path,
        //         autoOpenPoView,
        //     });
        //     return;
        // }

        // Any other status: fetch the previously saved counter PO details the same way.
        await openProcessedPoScreen(original);
        //M10-13811
    };

    useEffect(() => {
        if (!OrdersPoFiltersData?.company?.length) {
            getAllCompanyDetails()
                .then((res) => dispatch(ordPoActions.setOrdersPoFiltersData({ key: "company", value: res?.data })))
                .catch((err) => console.log(err));
            fetchPoStatusDetails()
                .then((res) => dispatch(ordPoActions.setOrdersPoFiltersData({ key: "status", value: res?.data?.data })))
                .catch((err) => console.log(err));
        }
    }, [dispatch]);

    useEffect(() => {
        if (OrderPoFilters?.company?.length) {
            fetchStockistFilterData(OrderPoFilters?.company?.[0]?.code)
                .then((res) => dispatch(ordPoActions.setOrdersPoFiltersData({ key: "stockist", value: res?.data?.data?.data })))
                .catch((err) => console.log(err));
        }
    }, [OrderPoFilters?.company]);

    // M10-15868: Counter filter options, scoped to the selected company like Stockist above.
    // FilterAccordion only supports a single displayKey (for both the row label and its search
    // match), so pre-combine name + code into one string rather than touching that shared component.
    useEffect(() => {
        if (OrderPoFilters?.company?.length) {
            getCounterList({ company_code: OrderPoFilters?.company?.[0]?.code })
                .then((res) => {
                    const counters = (res?.data?.data || []).map((item: any) => ({
                        ...item,
                        counter_display: `${item?.counter_name || "-"} (${item?.counter_code || "-"})`,
                    }));
                    dispatch(ordPoActions.setOrdersPoFiltersData({ key: "counter", value: counters }));
                })
                .catch((err) => console.log(err));
        }
    }, [OrderPoFilters?.company]);
    // M10-15868

    useEffect(() => {
        resetPagination();
    }, [OrderPoFilters]);

    //M10-15818
    useEffect(() => {
        resetPagination();
    }, [searchValue, searchCategory]);
    //M10-15818

    //M10-13717
    useEffect(() => {
        fetchPendingOrder();
    }, [pageIndex, pageSize, OrderPoFilters, searchValue, searchCategory]);
    //M10-13717

    // if (loading)
    //   return (
    //     <div className="flex items-center justify-center my-10">
    //       <Spinner />
    //     </div>
    //   );

    return (
        <div>
            <div className="flex items-center justify-end w-full gap-2">
                {/* M10-15818 */}
                <div className="flex items-center w-[400px] mt-2 rounded-md overflow-hidden border border-gray-300 bg-white">
                    <Select value={searchCategory} onValueChange={setSearchCategory}>
                        <SelectTrigger className="w-[160px] rounded-none border-none bg-white text-gray-700 focus:ring-0 focus:outline-none focus:shadow-none">
                            <SelectValue placeholder="Search By" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                            {poSearchOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Input
                        placeholder="Search"
                        debounceTime={1250}
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value.trim())}
                        containerClasses="w-full px-3 py-1 text-gray-700 border-none bg-white focus:outline-none focus:ring-0 focus:shadow-none"
                    />
                </div>
                {/* M10-15818 */}
                <div className="flex items-center justify-end float-right gap-2 relative">
                    <Button
                        className="gap-1 px-4 mt-2 ml-2 border "
                        onClick={() => {
                            setIsFiltersShown(true);
                        }}
                    >
                        {Object.values(OrderPoFilters).some((value) => value && value.length > 0) && (
                            <span className="absolute top-[0px] flex items-center justify-center text-white text-xs right-[90px] w-6 h-6 bg-red-500 rounded-full">
                                {Object.values(OrderPoFilters).filter((value) => value && value.length > 0).length}
                            </span>
                        )}
                        Filters <FilterIcon className="w-5 h-5" />
                    </Button>
                    <Button onClick={fetchPendingOrder} loaderSize="small" className="h-auto py-2 mt-2 border">
                        Retrieve
                    </Button>
                </div>
            </div>

            <Transition
                as={Fragment}
                show={isFiltersShown}
                enter="transition transition ease-in-out duration-200 transform"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transition  ease-in-out duration-200 transform"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
            >
                <div className="fixed right-0 inset-0 top-0 z-[500]">
                    <OrdersPOFilter
                        ref={ref}
                        companyDetails={OrdersPoFiltersData?.company || []}
                        pendingStatus={OrdersPoFiltersData?.status || []}
                        stockistDetails={OrdersPoFiltersData?.stockist || []}
                        counterDetails={OrdersPoFiltersData?.counter || []}
                        onCloseClick={() => setIsFiltersShown(false)}
                    />
                </div>
            </Transition>

            {/* PO-LIST-CARD-REDESIGN */}
            <section className="flex items-center justify-between mt-5 mb-6">
                <div>
                    <h4 className="text-2xl font-semibold text-[#49484C]">PO</h4>
                    <p className="text-base font-medium text-[#49484C] opacity-60">View and manage PO</p>
                </div>
            </section>

            {loading && (
                <div className="flex justify-center w-full py-5">
                    <Spinner />
                </div>
            )}

            {!loading && (
                <>
                    {(!orderPoData || orderPoData.length === 0) && <p className="mt-10 mb-10 text-3xl text-center">No data...</p>}

                    {orderPoData?.map((item: any) => {
                        const key = String(item?.poid_number ?? item?.stockist_po_attachment_id);
                        return (
                            <PoCard
                                key={key}
                                data={item}
                                onViewDetails={() => rowClickHandler({ original: item })}
                                onRefresh={fetchPendingOrder}
                                onOrderCodeClick={() => handleOrderCodeClick(item)}
                            />
                        );
                    })}

                    {pageCount > 1 && (
                        <div className="flex items-center justify-center mb-1 mt-7">
                            <Button
                                className="gap-2 text-sm font-medium bg-white border rounded-r-none"
                                disabled={pageIndex <= 1}
                                onClick={() => onPaginationChange({ pageSize, pageIndex: Math.max(pageIndex - 2, 0) })}
                            >
                                <img src={downArrow} className="rotate-90" alt="previous" /> Previous
                            </Button>
                            {generatePageNumbers(pageIndex, pageCount).map((pageNumber, index) => (
                                <Fragment key={index}>
                                    {pageNumber === "..." ? (
                                        <span className="mx-2">...</span>
                                    ) : (
                                        <Button
                                            className={`border rounded-none bg-white gap-2 ${
                                                pageIndex === pageNumber
                                                    ? "bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700"
                                                    : "hover:bg-gray-100 hover:text-gray-700"
                                            }`}
                                            onClick={() => onPaginationChange({ pageSize, pageIndex: Number(pageNumber) - 1 })}
                                        >
                                            {pageNumber}
                                        </Button>
                                    )}
                                </Fragment>
                            ))}
                            <Button
                                className="gap-2 text-sm font-medium bg-white border rounded-l-none"
                                disabled={pageIndex >= pageCount}
                                onClick={() => onPaginationChange({ pageSize, pageIndex: Math.min(pageIndex, pageCount - 1) })}
                            >
                                Next <img src={downArrow} className="-rotate-90" alt="next" />
                            </Button>
                        </div>
                    )}
                </>
            )}
            {/* PO-LIST-CARD-REDESIGN */}
            {/* M10-13811 */}
            <OrderSelectDialog
                isOpen={orderSelectState.isOpen}
                orderCodes={orderSelectState.orderCodes}
                onClose={() => setOrderSelectState({ isOpen: false, orderCodes: [] })}
                onSelect={(code) => {
                    const companyCode = orderSelectState.companyCode;
                    const autoOpenPoView = orderSelectState.autoOpenPoView === true;
                    const po_file_path = orderSelectState.po_file_path;
                    setOrderSelectState({ isOpen: false, orderCodes: [] });
                    openOrderEditScreen(code, companyCode,orderSelectState.stockist_po_attachment_id, autoOpenPoView, po_file_path);
                }}
            />
            {/* M10-13811 */}
        </div>
    );
};

export default PoOrderScreen;
//M10-12866